import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTenant } from "../../Contexts/TenantContext";
import { useAuth } from "../../Contexts/AuthContext";
import { useCart } from "../../Contexts/CartContext";
import { TenantPreference } from "../../Models/TenantPreference";
import { EditUserDataType } from "../../Enums/EditUserDataType";
import { FaChevronDown, FaLock, FaSignOutAlt } from "react-icons/fa";
import AdminNotificationBell from "../AdminNotificationBell/AdminNotificationBell";
import AdminMobileSidebar from "../AdminMobileSidebar/AdminMobileSidebar";
import EditUserDataFlow from "../EditUserDataFlow/EditUserDataFlow";

type FeatureKey = "enableOperatorsFeature" | "enableBrandsFeature" | "enableCategoriesFeature" | "enableModelsFeature";
interface MenuItem {
  to: string;
  label: string;
  requiresFlag?: boolean;
  requiresPreference?: FeatureKey;
}

const ADMIN_ITEMS: MenuItem[] = [
  { to: "/admin",                      label: "Início" },
  { to: "/admin/operadores",           label: "Operadores",           requiresPreference: "enableOperatorsFeature" },
  { to: "/admin/clientes",             label: "Clientes" },
  { to: "/admin/marcas",               label: "Marcas",               requiresPreference: "enableBrandsFeature" },
  { to: "/admin/categorias",           label: "Categorias",           requiresPreference: "enableCategoriesFeature" },
  { to: "/admin/modelos",              label: "Modelos",              requiresPreference: "enableModelsFeature" },
  { to: "/admin/produtos",             label: "Produtos" },
  { to: "/admin/pedidos",              label: "Pedidos" },
  { to: "/admin/cupons",               label: "Cupons" },
  { to: "/admin/whatsapp",             label: "WhatsApp" },
  { to: "/admin/faturamento",          label: "Faturamento" },
  { to: "/admin/balcao",               label: "Balcão" },
];

const OPERATOR_ITEMS: MenuItem[] = [
  { to: "/operador",             label: "Início" },
  { to: "/operador/clientes",    label: "Clientes" },
  { to: "/operador/pedidos",     label: "Pedidos" },
  { to: "/operador/whatsapp",    label: "WhatsApp" },
  { to: "/operador/balcao",      label: "Balcão" },
];

const AltAdminNavbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const moreMenuRef = useRef<HTMLLIElement | null>(null);
  const isMoreHoveredRef = useRef(false);

  const { tenant, tenantPreference, loading: isTenantLoading } = useTenant();
  const { user, isAdmin, isOperator, isGoogleUser, logout } = useAuth();
  const { clearCart } = useCart();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1920);
  const [isEditUserDataFlowOpen, setIsEditUserDataFlowOpen] = useState(false);
  const [editUserDataMethod, setEditUserDataMethod] = useState<EditUserDataType>(EditUserDataType.Password);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!showMore) {
      return;
    }
    const handleClick = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setShowMore(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") setShowMore(false); };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [showMore]);

  const handleLogout = async () => {
    const onCartPage = location.pathname.includes("/carrinho");
    if (!onCartPage) {
      clearCart();
    }
    await logout();
    if (!onCartPage) {
      navigate("/");
    }
  };

  const isActive = (to: string) =>
    to === "/admin" || to === "/operador"
      ? location.pathname === to
      : location.pathname === to || location.pathname.startsWith(`${to}/`);

  const handleNav = (to: string) => (e: React.MouseEvent) => {
    if (isActive(to)) {
      e.preventDefault();
      navigate(to, { replace: true, state: { refreshKey: Date.now() } });
    }
  };

  const linkClass = (to: string) =>
    `transition-colors hover:underline underline-offset-4 hover:text-primary-600 whitespace-nowrap ${
      isActive(to) ? "text-primary-600 underline" : ""
    }`;

  const filterItems = (items: MenuItem[]) =>
    items.filter((item) => {
      if (item.requiresFlag && !tenantPreference?.enableTechnicalAssistancePage) {
        return false;
      }
      if (item.requiresPreference) {
        return (tenantPreference?.[item.requiresPreference as keyof TenantPreference] as boolean | undefined) !== false;
      }
      return true;
    });

  const visibleCount =
    windowWidth >= 1700 ? 13 :
    windowWidth >= 1500 ? 11 :
    windowWidth >= 1300 ? 9 :
    windowWidth >= 1100 ? 7 : 6;

  const allItems = filterItems(isAdmin ? ADMIN_ITEMS : OPERATOR_ITEMS);
  const primaryItems = allItems.slice(0, visibleCount);
  const overflowItems = allItems.slice(visibleCount);

  const adminHome = isAdmin ? "/admin" : "/operador";

  const isReady = !!tenant && !isTenantLoading;
  if (!isReady) {
    return (
      <div className="w-full relative z-40 bg-white border-b shadow-sm animate-pulse">
        <div className="flex items-center px-10 lg:px-20 py-5 gap-4">
          <div className="h-10 w-32 bg-gray-200 rounded" />
          <div className="flex-1 h-4 bg-gray-100 rounded mx-10" />
          <div className="h-5 w-12 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Desktop */}
      <nav className="hidden lg:flex relative z-40 bg-white border-b shadow-sm items-center justify-between px-10 lg:px-20 py-5">
        {/* Left: Logo */}
        <div className="shrink-0 w-44">
          <Link to={adminHome}>
            {tenant?.logo ? (
              <img src={tenant.logo.url} alt="Logo" className="h-10 lg:h-12 object-contain" />
            ) : (
              <span className="text-xl font-bold text-primary-600">{tenant?.companyName}</span>
            )}
          </Link>
        </div>

        {/* Center: Menu items */}
        <ul className="flex items-center gap-6 text-sm font-medium text-black">
          {primaryItems.map((item) => (
            <li key={item.to}>
              <Link to={item.to} onClick={handleNav(item.to)} className={linkClass(item.to)}>
                {item.label}
              </Link>
            </li>
          ))}

          {overflowItems.length > 0 && (
            <li
              className="relative"
              ref={moreMenuRef}
              onPointerEnter={(e) => {
                if (e.pointerType === "mouse") {
                  isMoreHoveredRef.current = true;
                  setShowMore(true);
                }
              }}
              onPointerLeave={(e) => {
                if (e.pointerType === "mouse") {
                  isMoreHoveredRef.current = false;
                  setShowMore(false);
                }
              }}
            >
              <button
                onClick={() => setShowMore((v) => isMoreHoveredRef.current || !v)}
                className="flex items-center gap-2 transition-colors hover:underline underline-offset-4 hover:text-primary-600 text-sm font-medium"
                aria-expanded={showMore}
                aria-haspopup="true"
              >
                Mais
                <FaChevronDown className={`text-xs transition-transform ${showMore ? "rotate-180" : ""}`} />
              </button>
              {showMore && (
                // The top padding keeps the pointer inside the menu on its way down from the button
                <div className="absolute right-0 top-full pt-4 z-50">
                  <div className="relative bg-white rounded-md shadow-xl border min-w-[160px]">
                    <div className="absolute -top-2 right-4 w-4 h-4 bg-white rotate-45 border-l border-t border-gray-200" />
                    <ul className="relative divide-y divide-gray-100 py-1">
                      {overflowItems.map((item) => (
                        <li key={item.to}>
                          <Link
                            to={item.to}
                            onClick={(e) => { handleNav(item.to)(e); setShowMore(false); }}
                            className={`block px-4 py-2 text-sm font-medium transition-colors hover:bg-primary-120 hover:text-primary-600 ${isActive(item.to) ? "text-primary-600" : "text-black"}`}
                          >
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </li>
          )}
        </ul>

        {/* Right: Icons */}
        <div className="flex items-center gap-8 shrink-0 text-black">
          <AdminNotificationBell />
          {!isGoogleUser && (
            <button
              className="flex items-center justify-center w-5 h-5 transition-colors hover:text-primary-600"
              aria-label="Alterar senha"
              title="Alterar senha"
              onClick={() => { setEditUserDataMethod(EditUserDataType.Password); setIsEditUserDataFlowOpen(true); }}
            >
              <FaLock size={18} />
            </button>
          )}
          <button
            className="flex items-center justify-center w-5 h-5 transition-colors hover:text-primary-600"
            aria-label="Sair"
            title="Sair"
            onClick={handleLogout}
          >
            <FaSignOutAlt size={18} />
          </button>
        </div>
      </nav>

      {/* Mobile */}
      <nav className="lg:hidden relative z-40 bg-white border-b shadow-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="w-8 h-8 flex items-center justify-center text-black shrink-0"
            aria-label="Abrir menu"
          >
            <svg viewBox="0 0 24 24" width="22" height="22">
              <rect x="3" y="5" width="18" height="2" rx="1" fill="currentColor" />
              <rect x="3" y="11" width="18" height="2" rx="1" fill="currentColor" />
              <rect x="3" y="17" width="18" height="2" rx="1" fill="currentColor" />
            </svg>
          </button>

          <Link to={adminHome} className="absolute left-1/2 -translate-x-1/2">
            {tenant?.logo ? (
              <img src={tenant.logo.url} alt="Logo" className="h-10 object-contain" />
            ) : (
              <span className="font-bold text-primary-600">{tenant?.companyName}</span>
            )}
          </Link>

          <div className="flex items-center gap-4 shrink-0">
            <AdminNotificationBell />
            <span className="text-xs font-bold text-white bg-black px-2 py-1 rounded-full">
              {isOperator ? "Operador" : "Admin"}
            </span>
          </div>
        </div>
      </nav>

      <AdminMobileSidebar
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        items={allItems}
        isActive={isActive}
        handleNav={handleNav}
        handleLogout={handleLogout}
        isGoogleUser={isGoogleUser}
        onChangePassword={() => { setEditUserDataMethod(EditUserDataType.Password); setIsEditUserDataFlowOpen(true); }}
        userName={user?.firstName}
      />

      <EditUserDataFlow
        isOpen={isEditUserDataFlowOpen}
        onClose={() => setIsEditUserDataFlowOpen(false)}
        method={editUserDataMethod}
      />
    </>
  );
};

export default AltAdminNavbar;
